import { FC, useContext, useMemo } from 'react';

import { logout } from '@modules/user-helpers';
import { clearSignUpType } from '@modules/auth-helpers';

import { useWorkspaceUrls } from '@modules/use-workspace-urls';
import { useUser } from '@context/user-context';

import Router from 'next/router';
import Popover from '@components/Popover/Popover';
import Link from 'next/link';
import { WorkspaceImage } from './WorkspaceButton';

import styles from './WorkspacePopover.module.scss';
import { layoutContext } from '@context/layout';

import { analytics } from '@modules/analytics';
import { useTeamRole } from '@hooks/team-workspace';
import { useActorPlan } from '@hooks/actor/use-actor-plan';

interface WorkspacePopoverProps {
  show: boolean;
  onClose: () => void;
  targetNode: HTMLButtonElement | undefined;
  openUpgradeDialog: () => void;
  workspaceName: string;
}

function setWorkspacePopoverPosition(isCollapsed: boolean) {
  return {
    left: isCollapsed ? 16 : 20,
    top: isCollapsed ? 155 : 145,
    bottom: 'unset',
  };
}

const WorkspacePopover: FC<React.PropsWithChildren<WorkspacePopoverProps>> = ({
  show,
  onClose,
  targetNode,
  openUpgradeDialog,
  workspaceName,
}) => {
  const { state: navState } = useContext(layoutContext);

  const userDetails = useUser();
  const { org } = userDetails;

  const workspaceImage = org?.avatarUrl;
  const { settingsUrl, workspaceSettingsUrl, billingAndSubscriptionUrl } = useWorkspaceUrls();

  const { atLeastRole } = useTeamRole();
  const isTeamAdmin = atLeastRole('ADMIN');
  const { planType, maxPersonalCards, planName } = useActorPlan();

  const numberOfMaximumCards = useMemo(() => {
    if (planType === 'team') {
      return 'Unlimited';
    } else {
      return maxPersonalCards;
    }
  }, [maxPersonalCards, planType]);

  async function handleLogOut() {
    clearSignUpType();
    await logout();
    Router.push('/login');
  }

  return (
    <Popover
      positioner={() => setWorkspacePopoverPosition(navState.collapsed ?? false)}
      targetNode={targetNode}
      show={show}
      onClose={onClose}
      fixed
      preventOutsideInteraction
      onBlur={onClose}
    >
      <div className={styles['workspace-popover']}>
        <ul className={styles['workspaces']}>
          <li>
            <a className={styles['workspace-link']}>
              <div className={styles['workspace-image-container']}>
                <WorkspaceImage
                  url={workspaceImage}
                  placeholderText={workspaceName?.charAt(0)?.toUpperCase()}
                />
              </div>
              <div className={styles['workspace-details-container']}>
                <b className={styles['workspace-name']}>{workspaceName}</b>
                <ul className={styles['workspace-metadata']}>
                  <li>{planName}</li>
                  <li>{numberOfMaximumCards} cards</li>
                </ul>
              </div>
            </a>
          </li>
        </ul>
        {planType !== 'team' && (
          <button
            className={styles['workspace-popover-action-btn']}
            onClick={() => {
              analytics.track({
                name: 'workspacedropdownupgradeplan_clicked',
              });
              onClose();
              openUpgradeDialog();
            }}
          >
            Upgrade Blinq plan
          </button>
        )}

        {planType !== 'free' && (
          <Link href={billingAndSubscriptionUrl} legacyBehavior>
            <button className={styles['workspace-popover-action-btn']}>Billing & Subscription</button>
          </Link>
        )}

        <Link href={settingsUrl} legacyBehavior>
          <button className={styles['workspace-popover-action-btn']}>Account</button>
        </Link>

        {isTeamAdmin && (
          <Link href={workspaceSettingsUrl} legacyBehavior>
            <button className={styles['workspace-popover-action-btn']}>Team Settings</button>
          </Link>
        )}

        <button className={styles['workspace-popover-action-btn']} onClick={handleLogOut} data-type="danger">
          Log out
        </button>
      </div>
    </Popover>
  );
};

export { WorkspacePopover };
